import { useState, useMemo, useEffect, useCallback } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { z } from 'zod';
import { Button, Card, CardContent } from '@pantry/ui';
import {
  useItems,
  useStorageLocations,
  useResetTour,
  useViewPreferences,
  useUpdateViewPreferences,
  useAutoGroupSweep,
  type ItemWithInstanceCounts,
  type ItemQueryParams,
} from '@pantry/api-client';
import { toast } from 'sonner';
import { type ConsolidatedItem } from '@pantry/shared';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useTourContext } from '../../components/onboarding/useTourContext';
import { ItemFormDialog } from '../../components/items/item-form-dialog';
import { SetTargetDialog } from '../../components/inventory/SetTargetDialog';
import { AddToGroupDialog } from '../../components/inventory/AddToGroupDialog';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import {
  type Product,
  type InventorySortBy,
  type SortOrder,
  getDaysUntilExpiry,
  resolveDashboardExpiryDays,
  DEFAULT_VIEW_PREFERENCES,
} from '@pantry/shared';
import {
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  HomeIcon,
  QueueListIcon,
  QuestionMarkCircleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { InventoryFilterBar } from '../../components/items/inventory-filter-bar';
import { FlatItemsList, GroupedItemsList } from '../../components/items/items-list';

const searchSchema = z.object({
  add: z.boolean().optional(),
  productSearch: z.string().optional(),
  groupId: z.string().optional(),
});

export const Route = createFileRoute('/_app/items')({
  validateSearch: searchSchema,
  component: ItemsPage,
});

/**
 * Simple debounce hook for search input.
 */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function ItemsPage() {
  const { t } = useLingui();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();
  const { startTour } = useTourContext();
  const resetTour = useResetTour();
  const navigate = useNavigate();
  const { add: shouldOpenAdd, productSearch: initialProductSearch } = Route.useSearch();

  const householdId = selectedHousehold?.id || '';

  const [searchInput, setSearchInput] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [targetDialogState, setTargetDialogState] = useState<{
    open: boolean;
    mode: 'group' | 'item';
    entityId: string;
    entityName: string;
    currentQuantity?: number | null;
    currentUnit?: string | null;
  }>({ open: false, mode: 'item', entityId: '', entityName: '' });
  const [addToGroupState, setAddToGroupState] = useState<{
    open: boolean;
    productId: string;
    productName: string;
  }>({ open: false, productId: '', productName: '' });
  const [startingMiniTourId, setStartingMiniTourId] = useState<
    'inventory-basics' | 'inventory-adding' | null
  >(null);
  const [addDialogProductSearch, setAddDialogProductSearch] = useState<string | undefined>(
    undefined
  );

  // View preferences (sort + view mode)
  const { data: viewPrefs } = useViewPreferences('web');
  const updatePrefs = useUpdateViewPreferences();
  const viewMode = viewPrefs?.viewMode ?? DEFAULT_VIEW_PREFERENCES.viewMode;
  const sortBy = (viewPrefs?.sortBy ?? DEFAULT_VIEW_PREFERENCES.sortBy) as InventorySortBy;
  const sortOrder = (viewPrefs?.sortOrder ?? DEFAULT_VIEW_PREFERENCES.sortOrder) as SortOrder;

  const resolveProductName = useResolveProductName();
  const getDisplayName = useCallback(
    (item: ItemWithInstanceCounts) =>
      item.customName || (item.product ? resolveProductName(item.product as Product) : '') || '',
    [resolveProductName]
  );

  // Handle URL params to open add dialog
  useEffect(() => {
    if (shouldOpenAdd && selectedHousehold) {
      setShowAddForm(true);
      setAddDialogProductSearch(initialProductSearch);
      // Clear the search params after opening
      navigate({ to: '/items', search: {}, replace: true });
    }
  }, [shouldOpenAdd, initialProductSearch, selectedHousehold, navigate]);

  // Fire-and-forget auto-group sweep on mount
  const sweep = useAutoGroupSweep();

  useEffect(() => {
    if (householdId) {
      sweep.mutate(
        { householdId },
        {
          onSuccess: (result) => {
            if (result.grouped.length > 0) {
              toast.success(t`${result.grouped.length} items were auto-grouped`);
            }
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId]);

  // Debounce search input
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Build query params — hideDepleted=false to get zero-stock items for Depleted section
  const searchParams: ItemQueryParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      storageLocationId: locationFilter || undefined,
      hideDepleted: false,
    }),
    [debouncedSearch, locationFilter]
  );

  const {
    data: items,
    isLoading: itemsLoading,
    isFetching,
    error,
  } = useItems(householdId, searchParams);

  const { data: storageLocations } = useStorageLocations(householdId);

  const isLoading = householdLoading || itemsLoading;

  const handleSetTarget = useCallback((consolidated: ConsolidatedItem) => {
    const entityId = consolidated.groupId ?? consolidated.items[0]?.id;
    if (!entityId) return;
    setTargetDialogState({
      open: true,
      mode: consolidated.groupId ? 'group' : 'item',
      entityId,
      entityName: consolidated.displayName,
      currentQuantity: consolidated.targetQuantity,
      currentUnit: consolidated.targetUnit,
    });
  }, []);

  const handleAddToGroup = useCallback((consolidated: ConsolidatedItem) => {
    if (!consolidated.productId) return;
    setAddToGroupState({
      open: true,
      productId: consolidated.productId,
      productName: consolidated.displayName,
    });
  }, []);

  // Show empty state if no household selected
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-in">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <HomeIcon className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground mb-3">
            <Trans>Your Inventory</Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans>Select a household to view and manage your pantry items.</Trans>
          </p>
          <Button asChild size="lg">
            <Link to="/households">
              <PlusIcon className="h-5 w-5 mr-2" />
              <Trans>Get Started</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Loading state for first load
  if (isLoading && !items) {
    return (
      <div className="space-y-8 pb-8">
        {/* Header skeleton */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="h-10 w-48 animate-pulse rounded-lg bg-muted" />
            <div className="h-5 w-64 animate-pulse rounded bg-muted mt-2" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-32 animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-28 animate-pulse rounded-lg bg-muted" />
          </div>
        </div>
        {/* Search skeleton */}
        <Card>
          <CardContent className="py-4">
            <div className="flex gap-4">
              <div className="flex-1 h-10 animate-pulse rounded-lg bg-muted" />
              <div className="w-48 h-10 animate-pulse rounded-lg bg-muted" />
            </div>
          </CardContent>
        </Card>
        {/* Items skeleton */}
        <Card>
          <CardContent className="p-4">
            <div className="h-6 w-32 animate-pulse rounded bg-muted mb-4" />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !items) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-destructive/50">
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
              <Trans>Failed to load items</Trans>
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <Trans>Try Again</Trans>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasItems = items && items.length > 0;

  // Calculate summary stats
  const totalItems = items?.length ?? 0;
  const dashboardDays = resolveDashboardExpiryDays(selectedHousehold ?? null);
  const expiringSoonCount =
    items?.filter((item) => {
      if (!item.earliestExpiryDate) return false;
      const days = getDaysUntilExpiry(item.earliestExpiryDate);
      return days >= 0 && days <= dashboardDays;
    }).length ?? 0;

  const handleStartMiniTour = async (tourId: 'inventory-basics' | 'inventory-adding') => {
    if (startingMiniTourId) return;

    setStartingMiniTourId(tourId);
    try {
      await resetTour.mutateAsync({ tourId, deviceType: 'web' });
      await startTour(tourId);
    } catch (error) {
      console.error(`Failed to start mini tour ${tourId}:`, error);
    } finally {
      setStartingMiniTourId(null);
    }
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Page header */}
      <section
        className="animate-in"
        style={{ animationDelay: '0ms' }}
        data-tour="inventory-page-intro"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              <Trans>Inventory</Trans>
            </h1>
            <p className="text-muted-foreground mt-1">
              {totalItems > 0 ? (
                <>
                  <span className="font-medium text-foreground">{totalItems}</span>{' '}
                  <Trans>items</Trans>
                  {expiringSoonCount > 0 && (
                    <>
                      {' '}
                      &middot;{' '}
                      <span className="text-expiring-soon">
                        <Trans>{expiringSoonCount} expiring soon</Trans>
                      </span>
                    </>
                  )}
                  {selectedHousehold && <Trans> in {selectedHousehold.name}</Trans>}
                </>
              ) : selectedHousehold ? (
                t`Manage your pantry for ${selectedHousehold.name}`
              ) : (
                t`Manage your pantry`
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {householdId && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/households/$householdId/product-groups" params={{ householdId }}>
                  <Squares2X2Icon className="h-4 w-4 mr-2" />
                  <Trans>Manage Groups</Trans>
                </Link>
              </Button>
            )}
            <Button variant="outline" asChild data-tour="inventory-bulk-add">
              <Link to="/items/batch">
                <QueueListIcon className="h-4 w-4 mr-2" />
                <Trans>Add Multiple</Trans>
              </Link>
            </Button>
            <Button onClick={() => setShowAddForm(true)} data-tour="add-item">
              <PlusIcon className="h-4 w-4 mr-2" />
              <Trans>Add Item</Trans>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => void handleStartMiniTour('inventory-adding')}
              data-tour="inventory-help-adding"
              aria-label={t`Start adding items mini tour`}
              title={t`How to add items`}
              aria-busy={startingMiniTourId === 'inventory-adding'}
              disabled={startingMiniTourId === 'inventory-adding'}
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Search and filters */}
      <section className="animate-in" style={{ animationDelay: '100ms' }}>
        <InventoryFilterBar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          locationFilter={locationFilter}
          onLocationChange={setLocationFilter}
          storageLocations={storageLocations}
          viewMode={viewMode as 'grouped' | 'flat'}
          onViewModeChange={(mode) => updatePrefs.mutate({ platform: 'web', viewMode: mode })}
          sortBy={sortBy}
          onSortByChange={(value) => updatePrefs.mutate({ platform: 'web', sortBy: value })}
          sortOrder={sortOrder}
          onSortOrderChange={(order) => updatePrefs.mutate({ platform: 'web', sortOrder: order })}
          isFetching={isFetching}
          isStartingBasicsTour={startingMiniTourId === 'inventory-basics'}
          onStartBasicsTour={() => void handleStartMiniTour('inventory-basics')}
        />
      </section>

      {/* Add item dialog */}
      {showAddForm && (
        <ItemFormDialog
          householdId={householdId}
          defaultProductSearch={addDialogProductSearch}
          onClose={() => {
            setShowAddForm(false);
            setAddDialogProductSearch(undefined);
          }}
        />
      )}

      {/* Set Target dialog */}
      <SetTargetDialog
        open={targetDialogState.open}
        onOpenChange={(open) => setTargetDialogState((s) => ({ ...s, open }))}
        mode={targetDialogState.mode}
        entityId={targetDialogState.entityId}
        entityName={targetDialogState.entityName}
        householdId={householdId}
        currentQuantity={targetDialogState.currentQuantity}
        currentUnit={targetDialogState.currentUnit}
      />

      {/* Add to Group dialog */}
      <AddToGroupDialog
        open={addToGroupState.open}
        onOpenChange={(open) => setAddToGroupState((s) => ({ ...s, open }))}
        productId={addToGroupState.productId}
        productName={addToGroupState.productName}
        householdId={householdId}
      />

      {/* Items list */}
      <section
        className="animate-in"
        style={{ animationDelay: '200ms' }}
        data-tour="inventory-grouped-list"
      >
        {hasItems ? (
          viewMode === 'flat' ? (
            <FlatItemsList
              items={items}
              householdId={householdId}
              household={selectedHousehold ?? null}
              sortBy={sortBy}
              sortOrder={sortOrder}
              getDisplayName={getDisplayName}
              onSetTarget={handleSetTarget}
              onAddToGroup={handleAddToGroup}
            />
          ) : (
            <GroupedItemsList
              items={items}
              storageLocations={storageLocations || []}
              householdId={householdId}
              household={selectedHousehold ?? null}
              sortBy={sortBy}
              sortOrder={sortOrder}
              getDisplayName={getDisplayName}
              onSetTarget={handleSetTarget}
              onAddToGroup={handleAddToGroup}
            />
          )
        ) : (
          <Card>
            <CardContent className="py-16">
              <div className="text-center max-w-sm mx-auto">
                <div className="mx-auto mb-6 w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center">
                  <ArchiveBoxIcon className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  {searchParams.search || searchParams.storageLocationId ? (
                    <Trans>No items match your search</Trans>
                  ) : (
                    <Trans>Your pantry is empty</Trans>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {searchParams.search || searchParams.storageLocationId ? (
                    <Trans>Try adjusting your search terms or clearing the filter.</Trans>
                  ) : (
                    <Trans>Start by adding items to track what you have on hand.</Trans>
                  )}
                </p>
                {searchParams.search || searchParams.storageLocationId ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchInput('');
                      setLocationFilter('');
                    }}
                  >
                    <Trans>Clear Filters</Trans>
                  </Button>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button onClick={() => setShowAddForm(true)}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      <Trans>Add Item</Trans>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to="/items/batch">
                        <QueueListIcon className="h-4 w-4 mr-2" />
                        <Trans>Add Multiple</Trans>
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}
